import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import type { CustodyCalendarDay } from '@kinapp/shared';

interface CustodyCalendarProps {
  familyId: string;
  dependentId: string;
  familyMembers: Array<{ user: { id: string; name: string } }>;
}

const PARENT_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
  { bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500' },
  { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  { bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500' },
];

function getMonthDates(year: number, month: number) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startPad = first.getDay(); // 0 = Sunday
  const dates: (Date | null)[] = [];

  for (let i = 0; i < startPad; i++) dates.push(null);
  for (let d = 1; d <= last.getDate(); d++) dates.push(new Date(year, month, d));

  return dates;
}

function formatDateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

export function CustodyCalendar({ familyId, dependentId, familyMembers }: CustodyCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [calendar, setCalendar] = useState<CustodyCalendarDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCalendar();
  }, [currentMonth, familyId, dependentId]);

  const loadCalendar = async () => {
    try {
      setLoading(true);
      const startDate = new Date(currentMonth.year, currentMonth.month, 1);
      const endDate = new Date(currentMonth.year, currentMonth.month + 1, 0);

      const data = await api.getCustodyCalendar(
        familyId,
        dependentId,
        formatDateStr(startDate),
        formatDateStr(endDate)
      );
      setCalendar(data);
      setError('');
    } catch (err: any) {
      if (err.status === 404) {
        setCalendar([]);
        setError('');
      } else {
        setError(err.message || 'Failed to load calendar');
      }
    } finally {
      setLoading(false);
    }
  };

  const calendarMap = new Map(calendar.map((d) => [d.date, d]));

  // Build parent color map
  const parentIds = [...new Set(calendar.map((d) => d.parentId))];
  const colorMap = new Map(parentIds.map((id, i) => [id, PARENT_COLORS[i % PARENT_COLORS.length]]));

  const dates = getMonthDates(currentMonth.year, currentMonth.month);
  const today = formatDateStr(new Date());

  const monthLabel = new Date(currentMonth.year, currentMonth.month).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
  });

  const prevMonth = () => {
    setCurrentMonth((prev) => {
      const m = prev.month - 1;
      return m < 0 ? { year: prev.year - 1, month: 11 } : { year: prev.year, month: m };
    });
  };

  const nextMonth = () => {
    setCurrentMonth((prev) => {
      const m = prev.month + 1;
      return m > 11 ? { year: prev.year + 1, month: 0 } : { year: prev.year, month: m };
    });
  };

  if (loading && calendar.length === 0) {
    return <div className="text-gray-500 text-sm">Loading calendar...</div>;
  }

  if (error) {
    return <div className="text-red-600 text-sm">{error}</div>;
  }

  if (calendar.length === 0) {
    return (
      <div className="text-gray-500 text-sm">
        No custody schedule set up yet.
      </div>
    );
  }

  return (
    <div>
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-1 text-gray-500 hover:text-gray-700">&larr;</button>
        <h3 className="text-sm font-semibold text-gray-700">{monthLabel}</h3>
        <button onClick={nextMonth} className="p-1 text-gray-500 hover:text-gray-700">&rarr;</button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="text-xs text-center text-gray-500 font-medium py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {dates.map((date, i) => {
          if (!date) {
            return <div key={`pad-${i}`} className="h-10" />;
          }

          const dateStr = formatDateStr(date);
          const entry = calendarMap.get(dateStr);
          const isToday = dateStr === today;
          const colors = entry ? colorMap.get(entry.parentId) : null;

          return (
            <div
              key={dateStr}
              className={`h-10 rounded flex flex-col items-center justify-center text-xs relative ${
                colors ? `${colors.bg} ${colors.text}` : 'text-gray-400'
              } ${isToday ? 'ring-2 ring-primary-500' : ''}`}
              title={
                entry
                  ? `${entry.parentName || 'Parent'}${entry.isOverride ? ` (${entry.overrideReason || 'Override'})` : ''}`
                  : undefined
              }
            >
              <span className={`font-medium ${isToday ? 'font-bold' : ''}`}>{date.getDate()}</span>
              {entry?.isOverride && (
                <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-red-400" />
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-3">
        {parentIds.map((id) => {
          const colors = colorMap.get(id)!;
          const name = familyMembers.find((m) => m.user.id === id)?.user.name
            || calendar.find((d) => d.parentId === id)?.parentName
            || 'Parent';
          return (
            <div key={id} className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className={`w-3 h-3 rounded-full ${colors.dot}`} />
              {name}
            </div>
          );
        })}
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
          Override
        </div>
      </div>
    </div>
  );
}
